import { useCallback } from "react";
import { useRouter } from "expo-router";
import { HeaderBackButton } from "@react-navigation/elements";
import { useUnistyles } from "react-native-unistyles";
import { useBooleanFlag, FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";

/**
 * Hook that provides a standardized header back button for screens that need explicit back navigation.
 *
 * This is needed for screens under the tenant Stack navigator when navigating from outside
 * (e.g., from the Sites/Alarms tab), where the default back button doesn't appear because the screen
 * is the first in that Stack's history.
 *
 * The fallback defaults to "/sites" when site-wide controls are enabled, otherwise "/alarms".
 * This cross-navigator fallback is a workaround — ideally alarm-settings would be nested
 * within the Sites tab's own stack so back navigation works natively.
 *
 * @param fallbackRoute - Override the default fallback route
 * @returns headerLeft - A memoized component to pass to navigation.setOptions({ headerLeft })
 */
export function useHeaderBackButton(fallbackRoute?: string) {
  const router = useRouter();
  const { theme } = useUnistyles();
  const isSiteWideControlsEnabled = useBooleanFlag(FEATURE_FLAGS.SITE_WIDE_CONTROLS);
  const resolvedFallback = fallbackRoute ?? (isSiteWideControlsEnabled ? "/sites" : "/alarms");

  const handleBackPress = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.dismissTo(resolvedFallback as never);
  }, [router, resolvedFallback]);

  const headerLeft = useCallback(
    () => <HeaderBackButton onPress={handleBackPress} tintColor={theme.colors.text} />,
    [handleBackPress, theme.colors.text],
  );

  return { headerLeft };
}
